import classnames from "classnames"
import React, { useState, useEffect } from "react"
import { GetHiddenCharities, GetHiddenCatCharityList } from "../../../Code/Data"
import { filterCharities } from "../../../Code/Utilities"
import { useAppData } from "../../../Context/appData"
import CharityDetailModal from "../CharityDetailModal"
import LoadingSpinner from "../LoadingSpinner"
import CategoryFilter from "./CategoryFilter"
import styles from "./styles.module.scss"
import { useLocation } from "react-router-dom"
import { useRedemptionData } from "../../../Context/redemption"
import LocationFilter from "./LocationFilter"

export default function CharitySelector({
  isDirectDonation,
  color,
  query,
  charities,
  catId: catIdProp,
  catName: catNameProp,
  handleChangeCat: handleChangeCatProp,
  SHCID,
  onUpdate,
  fromRedemption,
  afterAddCharity,
  setSelCharityList,
  unlimitted,
  replaceMode,
  max = 3,
}) {
  const routerLocation = useLocation()
  const [searchQuery, setSearchQuery] = useState(query)
  const [viewCharity, setViewCharity] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const { redeemCodeInfo } = useRedemptionData()
  const [location, setLocation] = useState()
  // CharitySelector works in two modes:
  //  (a) "controlled" — the parent passes catId/catName/handleChangeCat.
  //  (b) "uncontrolled" — the parent passes none and CharitySelector owns
  //      its own category state. Two of the four call sites (UserRedemptionPage
  //      Step2 and CreateUserRedemptionPage Step4) don't pass a handleChangeCat,
  //      so without this internal state the category-filter dropdown silently
  //      threw on every click and the filter never applied.
  const [catId, setCatId] = useState(catIdProp ?? null)
  const [catName, setCatName] = useState(catNameProp ?? null)

  useEffect(() => {
    if (catIdProp !== undefined) setCatId(catIdProp ?? null)
  }, [catIdProp])
  useEffect(() => {
    if (catNameProp !== undefined) setCatName(catNameProp ?? null)
  }, [catNameProp])

  const onCategoryChange = (newCatId, newCatName) => {
    setCatId(newCatId ?? null)
    if (newCatName !== undefined) setCatName(newCatName ?? null)
    if (handleChangeCatProp) handleChangeCatProp(newCatId, newCatName)
  }
  const [filterByMainList] = useState()
  const [filterByTop50List, setFilterByTop50List] = useState()
  const [filterByTop10, setFilterByTop10] = useState()
  const [filterByMajorList, setFilterByMajorList] = useState()
  const { fullCharityList } = useAppData()
  const [listToDisplay, setListToDisplay] = useState(fullCharityList)
  const [noResults, setNoResults] = useState(false)

  const handleUpdateSearchQuery = e => {
    setSearchQuery(e.target.value)
  }
  const getHiddenCatCharities = async () => {
    const result = await GetHiddenCatCharityList(SHCID)
    if (result?.length > 0) {
      setListToDisplay(prev => [...(prev || []), ...result])
    }
  }
  const handleUpdate = charity => {
    const selected =
      charities.includes(charity.CharityId) || charities.includes(charity)
    if (
      !selected &&
      ((charities.length < max || !!unlimitted) || (charities.length === 1 && replaceMode))
    ) {
      setViewCharity(false)
      onUpdate(charity)
      if (afterAddCharity) {
        afterAddCharity()
      }
    }
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!!SHCID) {
      getHiddenCatCharities()
    }
    if (fullCharityList && charities && !fromRedemption) {
      const chars = fullCharityList.filter(charity =>
        charities.includes(charity.CharityId)
      )
      if (chars.length > 0) setSelCharityList(chars)
    }
    setIsLoading(!(!!fullCharityList && !!listToDisplay))
  }, [])

  useEffect(() => {
    if (fullCharityList && !listToDisplay) {
      setListToDisplay(fullCharityList)
    }
    setIsLoading(
      !(!!fullCharityList && fullCharityList?.length && !!listToDisplay)
    )
  }, [fullCharityList, listToDisplay])

  useEffect(() => {
    if (!isLoading) {
      getFilteredCharityList()
    }
  }, [
    filterByMajorList,
    filterByMainList,
    filterByTop10,
    filterByTop50List,
    searchQuery,
    routerLocation,
    location,
    catId,
    catName,
    isLoading,
  ])

  if (isLoading) {
    return <LoadingSpinner />
  }

  const getFilteredCharityList = async () => {
    let sortedList = [...fullCharityList]
    let filteredList = []

    if (
      !!redeemCodeInfo?.AddedCategoryIds &&
      redeemCodeInfo?.AddedCategoryIds.length > 0
    ) {
      let addedCatIdCharities = []
      for (let i = 0; i < redeemCodeInfo?.AddedCategoryIds?.length; i++) {
        let result = await GetHiddenCharities(
          redeemCodeInfo.AddedCategoryIds[i]
        )
        addedCatIdCharities = addedCatIdCharities.concat(result)
      }
      let list = redeemCodeInfo.OnlyShowAddedCategoryIds
        ? addedCatIdCharities
        : [...fullCharityList, ...addedCatIdCharities]
      let uniqueArr = [...new Set(list)]
      filteredList = uniqueArr
    } else {
      filteredList = [...fullCharityList]
    }
    const listname = filterByMainList ? "main" : "all"

    if (filterByMajorList) {
      filteredList = sortedList.filter(c => !c.IsLocal)
    }
    if (filterByTop50List) {
      filteredList = sortedList
        .filter(c => c.Rank === 1 || c.Rank === 2)
        .slice(0, 50)
    }
    if (filterByTop10) {
      filteredList = sortedList.filter(c => c.Rank === 2)
    }
    if (searchQuery) {
      filteredList = filteredList.filter(charity =>
        charity.CharityName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }
    // Resolve a category-name prop to its CategoryId (URL routes like
    // /Charities/Cat/:id?/:name? sometimes pass a name only).
    let effectiveCatId = catId
    if (!effectiveCatId && catName && fullCharityList?.length) {
      const match = fullCharityList.find(
        c => c.CategoryName && c.CategoryName.toUpperCase() === catName.toUpperCase()
      )
      effectiveCatId = match?.CategoryId
    }

    if (effectiveCatId || filterByMainList) {
      // NOTE: filterCharities signature is (list, searchTerm, categoryId, listName, searchDescription).
      // The previous call passed catName in the listName slot, which made the
      // inner OR-chain reject every charity and return [], so the category
      // filter silently did nothing.
      filteredList = filterCharities(
        filteredList,
        searchQuery,
        effectiveCatId,
        listname
      )
    }
    if (!!location && !filterByMainList) {
      filteredList = filteredList.filter(
        charity =>
          charity.IsLocal &&
          charity.Locations &&
          charity.Locations.includes(location)
      )
    }

    const uniqueFilteredList = [
      ...new Map(filteredList.map(c => [c.CharityName, c])).values(),
    ]
    setListToDisplay(uniqueFilteredList)
    setNoResults(!uniqueFilteredList.length)
  }

  return (
    <>
      <div className="container-fluid">
        <div
          className={classnames(
            styles.header,
            isDirectDonation ? styles.headerAuto : styles.headerConstrained
          )}>
          <div className={styles.headerRow}>
            <div className={styles.inputSearch} style={{ borderColor: color }}>
              <input
                className={styles.input}
                placeholder="Search"
                value={searchQuery}
                onChange={handleUpdateSearchQuery}
              />
              <i className="fa-solid fa-magnifying-glass" style={{ color }} />
            </div>
            <CategoryFilter
              categoryId={catId}
              categoryName={catName}
              handleChangeCat={onCategoryChange}
              color={color}
            />
            {!filterByMainList && (
              <LocationFilter
                location={location}
                setLocation={setLocation}
                color={color}
              />
            )}
          </div>

          <div className={styles.content}>
            <div className={styles.divFilters}>
              <div>
                <input
                  type="checkbox"
                  checked={filterByTop50List}
                  onChange={e => setFilterByTop50List(e.target.checked)}
                  name="filterByTop50List"
                />
                <label target="filterByTop50List">
                  Only display top causes
                </label>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={filterByTop10}
                  onChange={e => setFilterByTop10(e.target.checked)}
                  name="filterByTop10"
                />
                <label target="filterByTop10">
                  Only display favorite popular causes
                </label>
              </div>
              <div>
                <input
                  type="checkbox"
                  checked={filterByMajorList}
                  onChange={e => setFilterByMajorList(e.target.checked)}
                  name="filterByMajorList"
                />
                <label target="filterByMajorList">
                  Only display major charities
                </label>
              </div>
            </div>
            {(!isDirectDonation && max === 3) || unlimitted ? (
              <div className={styles.tip}>
                {unlimitted
                  ? "Choose your charities below."
                  : "Choose up to 3 charities to redeem below."}
              </div>
            ) : (
              <div className={styles.tip}>Select a charity below</div>
            )}

            <div
              className={styles.charityList}
            // style={{ width: isDirectDonation ? "46pc" : "100%" }}
            >
              {noResults ? (
                <div className={styles.noResults}>
                  No charities matched your search
                </div>
              ) : (
                listToDisplay.map(charity => {
                  const selected =
                    charities.includes(charity.CharityId) ||
                    charities.includes(charity)
                  return (
                    <div
                      key={charity.CharityId}
                      className={styles.charityItem}
                      onClick={() => !viewCharity && handleUpdate(charity)}
                      value={charity.CharityId}>
                      <div
                        className={styles.btnView}
                        onClick={event => {
                          event.stopPropagation()
                          setViewCharity(charity)
                        }}>
                        <i className="fa-solid fa-eye" />
                        View
                      </div>
                      <div
                        className={classnames(styles.charityName, {
                          [styles.selected]: selected,
                        })}
                        style={{ color: selected && color }}>
                        {charity.CharityName}
                      </div>
                      {selected && (
                        <div className={styles.iconSelected} style={{ color }}>
                          <i className="fa-solid fa-circle-check" />
                        </div>
                      )}
                    </div>
                  )
                }) || <LoadingSpinner />
              )}
            </div>
          </div>
        </div>
        <CharityDetailModal
          charity={viewCharity}
          onClose={() => setViewCharity()}
          onSelect={handleUpdate}
        />
      </div>
    </>
  )
}
